import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Paper } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { frFR, enUS } from '@mui/x-data-grid/locales';
import { formatDateTime, getGenderName } from '@utils/helpers';
import API from '@/utils/axios';
import theme from '@/theme';
import { useAuth } from '@contexts/AuthContext';
import useLocalStorage from '@/hooks/useLocalStorage';
import OurPagination from '@components/OurPagination';
import OurDataToolbar from '@components/OurDataToolbar';
import ContactSearch from '@components/contact/ContactSearch';
import ContactExtra from '@components/contact/ContactExtra';

function MessageTable({ url, type, onRowClick, ...props }) {
    const { t, i18n } = useTranslation();
    const { doLogout } = useAuth();
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [paginationModel, setPaginationModel] = useLocalStorage("messages.pagination", {
        pageSize: 10,
        page: 0,
    });
    const [sortModel, setSortModel] = useLocalStorage("messages.sort", [{
        field: 'date',
        sort: 'desc',
    }]);
    const [columnVisibility, setColumnVisibility] = useLocalStorage("messages.columns", {
        type: false,
        gender: false,
        first_name: false,
        last_name: false,
        lang: false,
        email: false,
        phone: false,
        extra: false,
    });
    const [density, setDensity] = useLocalStorage("messages.density", 'standard');
    const [searchVisible, setSearchVisible] = useLocalStorage("messages.searchVisible", false);
    const [filter, setFilter] = useLocalStorage("messages.searchValues", {});

    const toggleSearch = () => {
        if (searchVisible) {
            setFilter({});
        }
        setSearchVisible(!searchVisible);
    };

    const getFullName = (value, row) => {
        return `${row.first_name || ''} ${row.last_name || ''}`;
    };

    const loadRows = useCallback(async (paginationModel, sortModel) => {
        try {
            setLoading(true);
            let { field = '', sort = '' } = sortModel[0] || {};
            if (field === 'email_or_phone') {
                field = (type === 'email') ? 'email' : 'phone';
            }
            let params = {
                per_page: paginationModel.pageSize,
                page: paginationModel.page + 1,
                sort: field,
                direction: sort,
            };
            const response = await API.get(url, { params: { ...params, ...filter } });
            setRows(
                response.data.data.map((row) => ({
                    id: row.id,
                    type: row.type,
                    date: new Date(row.date),
                    credits: row.credits,
                    contact_id: row.contact_id,
                    lang: row.lang,
                    gender: row.gender,
                    first_name: row.first_name,
                    last_name: row.last_name,
                    email: row.email,
                    phone: row.phone,
                    extra: row.extra,
                }))
            );
            setRowCount(response.data.total);
            setLoading(false);
        } catch (error) {
            setLoading(false);
            if (error.response && error.response.status === 401) {
                doLogout();
            } else {
                console.error('Erreur de chargement', error);
            }
        }
    }, [url, type, doLogout, filter]);

    useEffect(() => {
        loadRows(paginationModel, sortModel);
    }, [loadRows, paginationModel, sortModel]);

    const localeTheme = i18n.language === 'fr' ? frFR : enUS;
    const themeWithLocale = createTheme(theme, localeTheme);

    const columns = [
        {
            field: 'id',
            headerName: t('ID'),
            flex: 0.5,
            type: 'number',
            maxWidth: 70,
        },
        {
            field: 'type',
            headerName: t('Type'),
            flex: 0.5,
            type: 'string',
            valueFormatter: (type) => t('credit.' + type),
        },
        {
            field: 'date',
            headerName: t('Date'),
            flex: 0.5,
            type: 'dateTime',
            valueFormatter: formatDateTime,
        },
        {
            field: 'email_or_phone',
            headerName: t('Recipient'),
            flex: 2,
            type: 'string',
            valueGetter: (value, row) => row.type === 'email' ? row.email : row.phone
        },
        {
            field: 'gender',
            headerName: t('Gender'),
            flex: 0.5,
            type: 'string',
            valueFormatter: (gender) => getGenderName(t, gender),
        },
        {
            field: 'first_name',
            headerName: t('First name'),
            flex: 1,
            type: 'string'
        },
        {
            field: 'last_name',
            headerName: t('Last name'),
            flex: 1,
            type: 'string'
        },
        {
            field: 'full_name',
            headerName: t('Full name'),
            flex: 2,
            type: 'string',
            valueGetter: getFullName
        },
        {
            field: 'email',
            headerName: t('Email'),
            flex: 2,
            type: 'string'
        },
        {
            field: 'phone',
            headerName: t('Phone'),
            flex: 2,
            type: 'string'
        },
        {
            field: 'lang',
            headerName: t('Language'),
            flex: 1,
            type: 'string',
            valueFormatter: (lang) => lang ? t('lang.' + lang) : ''
        },
        {
            field: 'extra',
            headerName: t('Extra data'),
            flex: 3,
            type: 'string',
            renderCell: (params) => <ContactExtra extra={params.value} />
        },
        {
            field: 'credits',
            headerName: t('Credits'),
            flex: 0.5,
            type: 'number',
            sortable: false,
        },
    ];

    return (
        <ThemeProvider theme={themeWithLocale}>
            <Paper {...props}>
                {searchVisible && <ContactSearch handleSubmit={setFilter} filter={filter} />}
                <DataGrid
                    autoHeight
                    loading={loading}
                    slots={{
                        toolbar: OurDataToolbar,
                        pagination: OurPagination,
                    }}
                    slotProps={{
                        toolbar: { searchVisible, toggleSearch },
                    }}
                    initialState={{
                        sorting: { sortModel }
                    }}
                    rows={rows}
                    columns={columns}
                    pageSizeOptions={[10, 15, 20, 50, 100]}
                    pagination
                    paginationMode="server"
                    rowCount={rowCount}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    columnVisibilityModel={columnVisibility}
                    onColumnVisibilityModelChange={(newModel) =>
                        setColumnVisibility(newModel)
                    }
                    sortingMode="server"
                    sortingOrder={['asc', 'desc']}
                    onSortModelChange={setSortModel}
                    density={density}
                    onDensityChange={setDensity}
                    disableRowSelectionOnClick
                    disableSelectionOnClick
                    disableColumnResize={true}
                    disableColumnFilter
                    onRowClick={onRowClick}
                />

            </Paper>
        </ThemeProvider>
    );
}

export default MessageTable;
